import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { compareWithMarketThunk } from "../../store/thunks/rateThunks";
import { ArrowUpIcon, ArrowDownIcon, MinusIcon } from "@heroicons/react/solid";

const MarketComparison = ({ branchCode }) => {
  const dispatch = useDispatch();
  const { marketData, loading, ui } = useSelector((state) => state.rate);
  const { user } = useSelector((state) => state.auth);

  // Use passed branchCode or fall back to selected branch or primary branch
  const currentBranch =
    branchCode || ui.selectedBranch || user?.branchAccess?.primaryBranch;

  useEffect(() => {
    if (currentBranch) {
      dispatch(compareWithMarketThunk({ branchCode: currentBranch }));
    }
  }, [dispatch, currentBranch]);

  const getDeviationColor = (deviation) => {
    const dev = parseFloat(deviation);
    if (Math.abs(dev) < 0.5) return "text-gray-600";
    if (dev > 0) return "text-green-600";
    return "text-red-600";
  };

  const getDeviationIcon = (deviation) => {
    const dev = parseFloat(deviation);
    if (Math.abs(dev) < 0.5)
      return <MinusIcon className="h-3 sm:h-4 w-3 sm:w-4" />;
    if (dev > 0) return <ArrowUpIcon className="h-3 sm:h-4 w-3 sm:w-4" />;
    return <ArrowDownIcon className="h-3 sm:h-4 w-3 sm:w-4" />;
  };

  if (loading.market) {
    return (
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-green-600 to-green-700">
        <h2 className="text-lg sm:text-xl font-bold text-white">
          Market Comparison
        </h2>
        <p className="text-green-100 text-xs sm:text-sm mt-1 truncate">
          Live market rates vs Your rates
          {currentBranch && (
            <span className="hidden sm:inline"> • Branch: {currentBranch}</span>
          )}
        </p>
        {currentBranch && (
          <p className="text-green-100 text-xs mt-0.5 sm:hidden">
            Branch: {currentBranch}
          </p>
        )}
      </div>

      <div className="p-4 sm:p-6">
        {!currentBranch ? (
          <div className="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
            Please select a branch to compare rates
          </div>
        ) : marketData.comparison && marketData.comparison.length > 0 ? (
          <div className="space-y-3 sm:space-y-4">
            {marketData.comparison.map((item) => (
              <div
                key={item.type}
                className="border rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow"
              >
                {/* Mobile Layout - Stacked */}
                <div className="sm:hidden">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-base">{item.type}</h3>
                    <div
                      className={`flex items-center ${getDeviationColor(
                        item.deviation
                      )}`}
                    >
                      {getDeviationIcon(item.deviation)}
                      <span className="ml-1 font-bold text-sm">
                        {item.deviation}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Your Rate:</span>
                      <span className="font-bold">
                        ₹{item.adminRate?.toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Market:</span>
                      <span className="font-bold">
                        ₹{item.marketRate?.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Tablet/Desktop Layout - Horizontal */}
                <div className="hidden sm:flex sm:items-center sm:justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg">{item.type}</h3>
                    <div className="flex flex-wrap items-center gap-x-4 lg:gap-x-6 mt-2 text-sm">
                      <div className="flex items-center">
                        <span className="text-gray-500">Your Rate:</span>
                        <span className="ml-2 font-bold">
                          ₹{item.adminRate?.toLocaleString("en-IN")}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-500">Market:</span>
                        <span className="ml-2 font-bold">
                          ₹{item.marketRate?.toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Deviation - Now with proper spacing */}
                  <div
                    className={`flex items-center ml-4 flex-shrink-0 ${getDeviationColor(
                      item.deviation
                    )}`}
                  >
                    {/* {getDeviationIcon(item.deviation)} */}
                    <span className="ml-1 font-bold text-lg">
                      {item.deviation}
                    </span>
                  </div>
                </div>

                {/* Warning message */}
                {Math.abs(parseFloat(item.deviation)) > 2 && (
                  <div className="mt-2 sm:mt-3 p-2 bg-yellow-50 rounded text-xs sm:text-sm text-yellow-800">
                    ⚠️{" "}
                    <span className="hidden sm:inline">
                      Significant deviation detected. Consider syncing with
                      market rates.
                    </span>
                    <span className="sm:hidden">
                      High deviation. Consider syncing.
                    </span>
                  </div>
                )}

                {/* Last update info */}
                {item.freshRate === false && (
                  <div className="mt-2 text-xs text-gray-500">
                    <span className="hidden sm:inline">
                      Market rate last updated:{" "}
                    </span>
                    <span className="sm:hidden">Updated: </span>
                    {new Date(item.lastMarketUpdate).toLocaleString("en-IN", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
            No market data available
          </div>
        )}

        <div className="mt-4 sm:mt-6 flex justify-center sm:justify-end">
          <button
            onClick={() =>
              dispatch(compareWithMarketThunk({ branchCode: currentBranch }))
            }
            disabled={!currentBranch}
            className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg 
              hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed 
              text-sm sm:text-base transition-colors duration-200 w-full sm:w-auto"
          >
            Refresh Comparison
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketComparison;
